import React, { Component } from 'react';
import { getCurrentLocale, strings } from '../../localization';
import CognitoUtil from '../aws/cognito/cognitoUtil';
import Events from '../common/events';
import HistoryUtil from '../common/historyUtil';
import Analytics from 'APP_ROOT/services/analytics';
import ProgressOverlay from '../common/ProgressOverlay';
import lsUtil from '../common/lsUtil';

const ROLES_MAP = {
    'owner': 'owner',
    'prime owner': 'owner',
    'admin': 'admin',
    'full': 'admin',
    'guest': 'guest',
    'guest + reports': 'guest_reports',
    'guest - no media': 'guest_no_media',
    'viewer': 'guest',    
    'viewer + reports': 'guest_reports',
    'viewer - no media': 'guest_no_media',
};
export default class SelectAccount extends Component {

    constructor(props) {
        super(props);
        this.state = {  
            error: null,
            showProgress: false,
            currentAccountId: null,
            isFilterVisible: false,
            filters: {
                name: '',
                email: '',
            },
            sort: {
                column: 'ravens',
                direction: 'desc'
            },
            redirectPath: null
        }
    }

    componentDidMount() {
        if (this.props.user) {
            this.initPage();            
        }
            
        if (lsUtil.getItem('raven.redirect_after_login')){        
            let redirectPathTempVariable = lsUtil.getItem('raven.redirect_after_login');
            
            if (redirectPathTempVariable.includes(getCurrentLocale())){
                let localeVar = "/" + getCurrentLocale();
                redirectPathTempVariable = redirectPathTempVariable.replace(localeVar, "");
            }
            this.setState({redirectPath: redirectPathTempVariable});
        }
        
    }

    componentDidUpdate(prevProps) {        
        if (!prevProps.user && this.props.user) {
            this.initPage();
        }        
    }

    initPage = () => {
        document.dispatchEvent(new CustomEvent(Events.SHOW_LOADING_PAGE, { 
            detail: { loadingPageMessage: strings.select_account_loading_your_accounts, override: true } 
        }));
        Analytics.pageView();

        if (!this.props.accounts) this.loadAccounts();
        else this.setCurrentAccountId();
    }

    loadAccounts = () => {

        this.props.getUserAccounts((accounts) => {

            if(!accounts || !Array.isArray(accounts)){
                console.error('SelectAccount: Error loading user accounts', accounts);
                document.dispatchEvent(new CustomEvent(Events.HIDE_LOADING_PAGE));
                window.location.href = '/?error=error_loading_accounts';
                return;
            }

            if(accounts.length === 0){
                HistoryUtil.push('/account/profile');
                return;
            }

            if(accounts.length === 1){
                this.props.onAccountSelected(accounts[0], this.state.redirectPath);
                return;
            }

            document.dispatchEvent(new CustomEvent(Events.HIDE_LOADING_PAGE));

            this.setCurrentAccountId();

        });
    }

    onAccountSelected = (e, account) => {
        e.preventDefault();
        this.setState({showProgress: true});
        this.props.onAccountSelected(account, this.state.redirectPath);
    }

    handleFilterChange = (e) => {
        this.setState({ 
            filters: { 
                ...this.state.filters, 
                [e.target.name]: e.target.value 
            } 
        });
    }

    handleSort = (column) => {
        this.setState(prevState => ({
            sort: {
                column: column,
                direction: prevState.sort.column === column && prevState.sort.direction === 'asc' ? 'desc' : 'asc'
            }
        }));
    }

    setCurrentAccountId = () => {
        document.dispatchEvent(new CustomEvent(Events.HIDE_LOADING_PAGE));
        
        if(this.props.user && this.props.user.currentAccount){
            this.setState({currentAccountId: this.props.user.currentAccount.accountId});
        }
        else {
            const userAccountDetails = CognitoUtil.getUserAccountsDetails();
            
            if(userAccountDetails){
                this.setState({currentAccountId: userAccountDetails.account.accountId});
            }
        }
    }

    toggleFilterVisibility = () => {
        this.setState(prevState => ({ isFilterVisible: !prevState.isFilterVisible }));
    }

    accountsFilterCallback = (account) => {
        const { filters, currentAccountId } = this.state;

        if (!account) {
            return false;
        }

        // Always include the current account
        if (account.accountId === currentAccountId) {
            return true;
        }

        const name = account.name ?? '';
        const email = account.email ?? '';

        const nameMatch = name.toLowerCase().includes(filters.name.toLowerCase());
        const emailMatch = email.toLowerCase().includes(filters.email.toLowerCase());
        return nameMatch && emailMatch;
    }

    accountsSortCallback = (a, b) => {
        const { sort, currentAccountId } = this.state;

        if (!a) return -1; // 'a' is null, so it comes first
        if (!b) return 1;  // 'b' is null, so it comes first
        
        // Always put the current account at the top
        if (a.accountId === currentAccountId) return -1;
        if (b.accountId === currentAccountId) return 1;

        let valA, valB;

        if (sort.column === 'ravens') {
            valA = a && isFinite(a.ravenCount) ? Number(a.ravenCount) : 0;
            valB = b && isFinite(b.ravenCount) ? Number(b.ravenCount) : 0;
        } 
        else {
            valA = (a && a[sort.column] ? a[sort.column] : '').toString().toLowerCase();
            valB = (b && b[sort.column] ? b[sort.column] : '').toString().toLowerCase();
        }

        if (valA < valB) return sort.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sort.direction === 'asc' ? 1 : -1;
        return 0;
    }

    renderSortIcon = (column) => {
        const { sort } = this.state;

        if (sort.column !== column) {
            return null;
        } 

        if(sort.direction === 'asc') {
            return <i className="material-icons">arrow_upward</i>
        }  
        
        return <i className="material-icons">arrow_downward</i>;
    }

    getLocalizedRole = (role) => {
        if (!role) return '';
        
        const trimmedRole = role.trim().toLowerCase();
        
        const localizationKey = ROLES_MAP[trimmedRole];
        
        // If not found, return the role as is
        if (!localizationKey) {
            console.warn(`No localization mapping found for role: "${trimmedRole}"`);
            return trimmedRole;
        }
        
        return strings[`select_account_role_${localizationKey}`] || trimmedRole;
    }

    getAccountRoles = (account) => {
        if(!account) return '';

        const {email = '', role = ''} = account;
        
        if (!role.trim()) return '';

        const isOwner = this.props.user?.email === email;
        
        if (isOwner) {
            return strings.select_account_role_owner;
        }
        
        return role
            .split(',')
            .map(r => this.getLocalizedRole(r))
            .join(', ');
    }

    render() {

        const { filters, isFilterVisible } = this.state;

        if(!this.props.user || !this.props.accounts){
            return null;
        }

        let processedAccounts = [];
        let placeholderCount = 0;

        const filtered = this.props.accounts.filter(this.accountsFilterCallback);
        processedAccounts = [...filtered].sort(this.accountsSortCallback);
        placeholderCount = this.props.accounts.length - processedAccounts.length;


        return (
            <div className="container select-account-page">
                <div className='row'>
                    <div className='col-12 col-lg-8 offset-lg-2'>
                        <h1>
                            <i className='material-icons'>switch_account</i>
                            {this.state.currentAccountId ? strings.change_account : strings.select_account}
                        </h1>
                    
                        <div className='accounts-list'>

                            {/* header */}
                            <div className='accounts-list-header row'>
                                <div className='col-4 col-md-3 account-name'><button className="btn btn-outline-transparent" onClick={() => this.handleSort('name')}>{strings.select_account_name} {this.renderSortIcon('name')}</button></div>
                                <div className='col-md-4 account-owner'><button className="btn btn-outline-transparent" onClick={() => this.handleSort('email')}>{strings.select_account_owner} {this.renderSortIcon('email')}</button></div>
                                <div className='col-3 col-md-2 account-ravens'><button className="btn btn-outline-transparent" onClick={() => this.handleSort('ravens')}>{strings.select_account_number_of_ravens} {this.renderSortIcon('ravens')}</button></div>
                                <div className='col-5 col-md-3 account-role'><button className="btn btn-outline-transparent" onClick={() => this.handleSort('role')}>{strings.select_account_role} {this.renderSortIcon('role')}</button></div>
                            </div>

                            {/* filters */}
                            <div className={`accounts-list-filter row ${isFilterVisible ? 'expanded' : ''}`}>
                            {isFilterVisible && (
                                <>
                                    <div className='col-4 col-md-3 account-name'>
                                        <input type="text" autoComplete="off" name="name" className="form-control" placeholder={strings.placeholder_filter} value={filters.name} onChange={this.handleFilterChange} />
                                    </div>
                                    <div className='col-md-4 account-owner'>
                                        <input type="text" autoComplete="off" name="email" className="form-control" placeholder={strings.placeholder_filter} value={filters.email} onChange={this.handleFilterChange} />
                                    </div>
                                    <div className='col-3 col-md-2 account-ravens'></div>
                                    <div className='col-5 col-md-3 account-role'></div>
                                </>)
                            }
                                <div className='filter-toggle-container'>
                                    <button className="btn btn-outline-transparent" onClick={this.toggleFilterVisibility} title={strings.placeholder_filter} data-testid="filter-toggle-button">
                                        <i className="material-icons">{isFilterVisible ? 'filter_alt_off' : 'filter_alt'}</i>
                                    </button>
                                </div>
                            </div>

                            <div className='accounts-list-body row'>
                                {processedAccounts.length > 0 ? (
                                    processedAccounts.map((account) => { 
                                        const accountId = account.accountId || '-';
                                        const accountName = account.name || '-';
                                        const accountEmail = account.email || '-';
                                        const ravensCount = isFinite(account.ravenCount) ? Number(account.ravenCount) : 0;
                                        const accountRoles = this.getAccountRoles(account);

                                        return (
                                            <div key={accountId} className={`col-12 account ${accountId === this.state.currentAccountId ? "current" : ""}`}>
                                                <div className='row'>
                                                    <div className='col-4 col-md-3 account-name'>
                                                        {accountId !== this.state.currentAccountId ? 
                                                            <button className="btn btn-outline-transparent" onClick={(e) => this.onAccountSelected(e, account)}>{accountName}</button> 
                                                        : 
                                                            <span>{accountName} ({strings.select_account_current})</span>
                                                        }
                                                    </div>
                                                    <div className='col-md-4 account-owner'><div>{accountEmail}</div></div>
                                                    <div className='col-3 col-md-2 account-ravens'>{ravensCount}</div>
                                                    <div className='col-5 col-md-3 account-role'>{accountRoles}</div>
                                                </div>
                                            </div>
                                        )
                                    })
                                ) :
                                    <div className="col-12 text-center no-results">{strings.select_account_no_results}</div>
                                }
                                {/* placeholder rows for excluded accounts to maintain consistent height */}
                                {Array.from({ length: placeholderCount }).map((_, index) => (
                                    <div key={`placeholder-${index}`} className="col-12 account placeholder">
                                        <div className='row'>
                                            <div className='col-4 col-md-3 account-name'>&nbsp;</div>
                                            <div className='col-md-4 account-owner'>&nbsp;</div>
                                            <div className='col-4 col-md-3 account-ravens'>&nbsp;</div>
                                            <div className='col-4 col-md-2 account-role'>&nbsp;</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {this.state.currentAccountId && (
                            <div className='actions'>
                                <button className='btn btn-outline-blue' onClick={(e) => this.props.cancelSelectAccount(e)}>
                                    <i className="material-icons">close</i>
                                    {strings.app_cancel}
                                </button>
                            </div>)
                        }

                        {this.state.error && (
                            <div className="error-message">
                                {this.state.error.toString()}
                            </div>
                        )}
                    </div>
                </div>
            {this.state.showProgress && <ProgressOverlay/>}
            </div>
        );
    }
}